import { countTextLength } from '@src/services/tts';
import { BREAK_TIME_REGEX } from '@src/constants/voice';

/**
 * Find voice usage entry in array format
 * @param {Array} usageArray - Array of usage objects: [{ code: "voiceCode", credits: number, times: number }]
 * @param {string} voiceCode - Voice code to find
 * @returns {Object|null} Usage entry or null if not found
 */
const findVoiceUsageInArray = (usageArray, voiceCode) => {
  if (!Array.isArray(usageArray) || !voiceCode) return null;

  const usageObj = usageArray.find((item) => item?.code === voiceCode);

  return usageObj;
};

/**
 * Update or add voice usage in array format
 * @param {Array} usageArray - Array of usage objects: [{ code: "voiceCode", credits: number, times: number }]
 * @param {string} voiceCode - Voice code to update
 * @param {Object} updates - Updates to apply: { credits, times }
 * @returns {Array} New array with updated usage
 */
const updateVoiceUsageInArray = (usageArray, voiceCode, updates) => {
  const newArray = Array.isArray(usageArray) ? [...usageArray] : [];

  // Try to find and update existing entry
  const foundIndex = newArray.findIndex((item) => item?.code === voiceCode);

  if (foundIndex >= 0) {
    // Update existing entry
    newArray[foundIndex] = {
      ...newArray[foundIndex],
      ...updates,
    };
  } else {
    // If not found, add new entry
    newArray.push({
      code: voiceCode,
      credits: 0,
      times: 0,
      ...updates,
    });
  }

  return newArray;
};

export const getVoiceTrialUsageByCode = (voice, trialVoiceUsage) => {
  if (!voice?.code || !trialVoiceUsage?.usage) return null;
  const voiceCode = voice?.code;

  return findVoiceUsageInArray(trialVoiceUsage.usage, voiceCode);
};

export const isVoiceEligibleForTrial = (voice, config) => {
  const enableVoiceTrial = Boolean(config);
  if (!enableVoiceTrial) return false;
  if (!voice) return false;

  const allowedFeatures = Array.isArray(config.allowedFeatures)
    ? config.allowedFeatures.filter(Boolean)
    : [];

  if (!allowedFeatures.length) return true;

  const voiceFeatures = Array.isArray(voice?.features) ? voice.features : [];

  return voiceFeatures.some((feature) => allowedFeatures.includes(feature));
};

export const isVoiceTrialLimitReached = (voice, config, usage) => {
  if (!isVoiceEligibleForTrial(voice, config)) return false;

  const usageEntry = getVoiceTrialUsageByCode(voice, usage);
  if (!usageEntry) return false;

  const maxTimes = config?.maxTimes;
  const maxCredits = config?.maxCredits;

  const usedTimes = usageEntry?.times || 0;
  const usedCredits = usageEntry?.credits || 0;

  const timesReached =
    typeof maxTimes === 'number' && maxTimes > 0 && usedTimes >= maxTimes;
  const creditsReached =
    typeof maxCredits === 'number' &&
    maxCredits > 0 &&
    usedCredits >= maxCredits;

  return timesReached || creditsReached;
};

export const willExceedTrialLimit = (
  voice,
  config,
  usage,
  pendingCredits = 0,
  pendingTimes = 1,
) => {
  if (!isVoiceEligibleForTrial(voice, config)) return false;

  const usageEntry = getVoiceTrialUsageByCode(voice, usage);
  const maxTimes = config?.maxTimes;
  const maxCredits = config?.maxCredits;

  // If no usage entry exists, check if pending request will exceed limit
  const usedTimes = usageEntry?.times || 0;
  const usedCredits = usageEntry?.credits || 0;

  // Check if adding pendingTimes will exceed maxTimes
  const willExceedTimes =
    typeof maxTimes === 'number' &&
    maxTimes > 0 &&
    usedTimes + pendingTimes > maxTimes;

  // Check if adding pendingCredits will exceed maxCredits
  const willExceedCredits =
    typeof maxCredits === 'number' &&
    maxCredits > 0 &&
    usedCredits + pendingCredits > maxCredits;

  return willExceedTimes || willExceedCredits;
};

/**
 * Calculate credits map from sentences grouped by voice code
 * @param {Array} sentences - Array of sentence objects with voice and text
 * @returns {Object} Map of voiceCode -> { voice, credits }
 */
export const calculateVoiceCreditsMap = (sentences = []) => {
  const voiceCreditsMap = {};

  sentences.forEach((sentence) => {
    const sentenceVoice = sentence?.voice;
    if (!sentenceVoice?.code) return;

    // Use countTextLength to calculate characters, fallback to sentence.characters or 0
    const text = sentence?.text || '';
    const characters = text
      ? countTextLength(text, BREAK_TIME_REGEX)
      : sentence?.characters ?? 0;
    const creditFactor = sentenceVoice?.creditFactor || 1;
    const credits = characters * creditFactor;

    if (!voiceCreditsMap[sentenceVoice.code]) {
      voiceCreditsMap[sentenceVoice.code] = {
        voice: sentenceVoice,
        credits: 0,
      };
    }
    voiceCreditsMap[sentenceVoice.code].credits += credits;
  });

  return voiceCreditsMap;
};

/**
 * Check if a voice is using trial (eligible but user doesn't have feature)
 * @param {Object} voice - Voice object
 * @param {Object} config - Trial config
 * @param {Array} userFeatures - User's features array
 * @param {Function} checkFeaturePermission - Function to check feature permission
 * @returns {boolean}
 */
export const isUsingTrialVoice = (
  voice,
  config,
  userFeatures = [],
  checkFeaturePermission,
) => {
  if (!voice || !checkFeaturePermission) return false;

  const isTrialEligible = isVoiceEligibleForTrial(voice, config);
  if (!isTrialEligible) return false;

  const voiceFeatures = Array.isArray(voice?.features) ? voice.features : [];
  if (!voiceFeatures.length) return false;

  // Check if user has at least one feature in voice.features
  const canUseVoice = voiceFeatures.some((feature) =>
    checkFeaturePermission(userFeatures, feature),
  );

  return !canUseVoice;
};

/**
 * Find all voices that will exceed trial limit in sentences mode
 * @param {Object} voiceCreditsMap - Map of voiceCode -> { voice, credits, times }
 * @param {Object} config - Trial config
 * @param {Object} usage - Trial usage object
 * @param {Array} userFeatures - User's features array
 * @param {Function} checkFeaturePermission - Function to check feature permission
 * @returns {Array} Array of exceeded voices with credits
 */
export const findExceededTrialVoices = (
  voiceCreditsMap,
  config,
  usage,
  userFeatures = [],
  checkFeaturePermission,
) => {
  if (!voiceCreditsMap || !config || !checkFeaturePermission) return [];

  return Object.values(voiceCreditsMap).filter(
    ({ voice: trialVoice, credits, times = 1 }) => {
      const isUsingTrial = isUsingTrialVoice(
        trialVoice,
        config,
        userFeatures,
        checkFeaturePermission,
      );

      return (
        isUsingTrial &&
        willExceedTrialLimit(trialVoice, config, usage, credits, times)
      );
    },
  );
};

export const buildUpdatedTrialUsage = ({
  currentUsage,
  voices = [],
  config,
  userFeatures = [],
}) => {
  if (!config || !voices.length) return null;

  const allowedFeatures = Array.isArray(config.allowedFeatures)
    ? config.allowedFeatures.filter(Boolean)
    : [];
  if (!allowedFeatures.length) return null;

  // Initialize baseUsage with array format
  const baseUsage =
    currentUsage && typeof currentUsage === 'object'
      ? {
          ...currentUsage,
          usage: Array.isArray(currentUsage.usage)
            ? [...currentUsage.usage]
            : [],
        }
      : { usage: [] };

  let hasChanges = false;
  const userFeatureSet = new Set(userFeatures.filter(Boolean));

  voices.forEach(({ voice, credits = 0 }) => {
    if (!voice?.code) return;

    const voiceFeatures = Array.isArray(voice?.features) ? voice.features : [];

    const isAllowed = voiceFeatures.some((feature) =>
      allowedFeatures.includes(feature),
    );
    if (!isAllowed) return;

    const lacksFeature = voiceFeatures.some(
      (feature) =>
        allowedFeatures.includes(feature) && !userFeatureSet.has(feature),
    );

    if (!lacksFeature) return;

    // Find current usage or default to 0
    const current = findVoiceUsageInArray(baseUsage.usage, voice.code) || {
      times: 0,
      credits: 0,
    };

    // Update usage in array format
    baseUsage.usage = updateVoiceUsageInArray(baseUsage.usage, voice.code, {
      times: (current.times || 0) + 1,
      credits: (current.credits || 0) + Math.max(credits, 0),
    });
    hasChanges = true;
  });

  if (!hasChanges) return null;

  return baseUsage;
};

/**
 * Rollback trial usage by subtracting the credits and times that were previously added
 * @param {Object} currentUsage - Current trial usage state
 * @param {Array} voices - Array of { voice, credits, times } to rollback
 * @param {Object} config - Trial config
 * @returns {Object|null} Updated trial usage or null if no changes
 */
export const rollbackTrialUsage = ({ currentUsage, voices = [], config }) => {
  if (!config || !voices.length) return null;

  // Initialize baseUsage with array format
  const baseUsage =
    currentUsage && typeof currentUsage === 'object'
      ? {
          ...currentUsage,
          usage: Array.isArray(currentUsage.usage)
            ? [...currentUsage.usage]
            : [],
        }
      : { usage: [] };

  let hasChanges = false;

  voices.forEach(({ voice, credits = 0, times = 1 }) => {
    if (!voice?.code) return;

    const current = findVoiceUsageInArray(baseUsage.usage, voice.code);
    if (!current) return; // No usage to rollback

    const newUsedTimes = Math.max(0, (current.times || 0) - times);
    const newUsedCredits = Math.max(0, (current.credits || 0) - credits);

    // Check if there's an actual change
    if (
      newUsedTimes !== (current.times || 0) ||
      newUsedCredits !== (current.credits || 0)
    ) {
      // Update usage in array format
      baseUsage.usage = updateVoiceUsageInArray(baseUsage.usage, voice.code, {
        times: newUsedTimes,
        credits: newUsedCredits,
      });
      hasChanges = true;
    }
  });

  if (!hasChanges) return null;

  return baseUsage;
};
